import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';

const VerifyEmailPending: React.FC = () => {
  const navigate = useNavigate();
  const { firebaseUser, vellrixUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Note: Navigation is handled by ProtectedRoute in App.tsx
  // No need to redirect here - user will be redirected automatically if verified

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleCheckVerification = async () => {
    setChecking(true);
    try {
      // Reload the Firebase user to get latest email verification status
      await auth.currentUser?.reload();
      const user = auth.currentUser;

      if (user?.emailVerified) {
        // Email is verified! Try to login to sync with backend
        window.location.reload(); // This will trigger the auth context to refresh
      } else {
        alert('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error checking verification:', error);
      alert('Error checking verification status. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;

    try {
      if (auth.currentUser) {
        const { sendEmailVerification } = await import('firebase/auth');
        await sendEmailVerification(auth.currentUser);
        alert('Verification email sent! Please check your inbox.');
        setResendCooldown(60); // 60 second cooldown
      }
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      if (error.code === 'auth/too-many-requests') {
        alert('Too many requests. Please wait a few minutes before trying again.');
      } else {
        alert('Failed to send verification email. Please try again later.');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ background: 'var(--bg-primary)' }}
    >
      <div className="max-w-md w-full space-y-8">
        <div className="rounded-lg shadow-md p-8" style={{ background: 'var(--bg-secondary)' }}>
          <div className="text-center">
            {/* Email Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
              <svg
                className="h-8 w-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Verify Your Email Address
            </h2>

            <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
              We've sent a verification email to:
            </p>

            <p className="mb-6 font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
              {firebaseUser?.email}
            </p>

            <div
              className="mb-6 p-4 rounded-lg text-left"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}
            >
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>Next steps:</strong>
              </p>
              <ol className="text-sm space-y-2 ml-4 list-decimal" style={{ color: 'var(--text-secondary)' }}>
                <li>Check your inbox for an email from Firebase</li>
                <li>Click the verification link in the email</li>
                <li>Return here and click "I've Verified My Email"</li>
              </ol>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleCheckVerification}
                disabled={checking}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checking ? 'Checking...' : "I've Verified My Email"}
              </button>

              <button
                onClick={handleResendVerification}
                disabled={resendCooldown > 0}
                className="w-full flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-color)',
                  background: 'transparent'
                }}
              >
                {resendCooldown > 0
                  ? `Resend Email (${resendCooldown}s)`
                  : "Didn't Receive Email? Resend"}
              </button>
            </div>

            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                onClick={handleLogout}
                className="text-sm hover:underline"
                style={{ color: 'var(--text-secondary)' }}
              >
                Sign out and use a different account
              </button>
            </div>

            <div
              className="mt-6 p-3 rounded text-xs text-left"
              style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}
            >
              <strong>Note:</strong> Check your spam folder if you don't see the email. The email may take
              a few minutes to arrive.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPending;
